import { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { showToast } from '@/components/ui/show-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useUploadFileMutation } from '@/services/files/queries';
import { useUpdateOrganisationMediaMutation } from '@/services/organisations/queries';
import type { OrganisationMedia } from '@/services/types';

type MediaModalProps = {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  organisationId?: string;
};

const UploadMediaModal = (props: MediaModalProps) => {
  const { isModalOpen, setIsModalOpen, organisationId } = props;
  const [activeTab, setActiveTab] = useState('upload');
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const { mutate: uploadFile, isPending: isUploading } = useUploadFileMutation(
    (response) => {
      const mediaData: OrganisationMedia = {
        url: response.url,
        name: form.getValues('name'),
        description: form.getValues('description') || ''
      };
      
      // Update organisation media with the uploaded file URL
      updateOrganisationMedia({ media: [mediaData] });
    },
    (error) => {
      showToast('error', 'Failed to upload file. Please try again.');
      console.error('Upload error:', error);
    }
  );

  const { mutate: updateOrganisationMedia, isPending: isUpdatingMedia } = useUpdateOrganisationMediaMutation(
    organisationId || '',
    () => {
      showToast('success', 'Media file uploaded and saved successfully');
      setIsModalOpen(false);
      form.reset();
      setPreviewUrl(null);
    },
    (error) => {
      showToast('error', 'Failed to save media information. Please try again.');
      console.error('Media update error:', error);
    }
  );

  const mediaFormSchema = z
    .object({
      name: z.string().min(1, 'Name is required'),
      description: z.string(),
      url: z.string().optional(),
      file: z.instanceof(File).optional()
    })
    .superRefine((data, ctx) => {
      // Validate that either URL or file is provided
      if (!data.url && !data.file) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Either URL or file must be provided',
          path: ['url']
        });
      }

      // If activeTab is not 'link', validate description
      if (activeTab !== 'link' && !data.description) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Description is required when uploading a file',
          path: ['description']
        });
      }

      // Validate file size if file is provided
      if (data.file && data.file.size > 2 * 1024 * 1024) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `File size (${formatFileSize(data.file.size)}) must be less than 2MB`,
          path: ['file']
        });
      }
    });

  const form = useForm({
    resolver: zodResolver(mediaFormSchema),
    defaultValues: {
      name: '',
      description: '',
      url: '',
      file: undefined
    }
  });

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      handleFileSelection(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      handleFileSelection(file);
    }
  };

  const handleFileSelection = (file: File) => {
    // Check file type
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      showToast('error', 'Please upload a PNG or JPG file');
      return;
    }

    // Check file size (2MB = 2 * 1024 * 1024 bytes)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      showToast('error', `File size (${formatFileSize(file.size)}) must be less than 2MB`);
      return;
    }

    // File is valid, proceed with upload
    form.setValue('file', file as any);
    form.setValue('name', file.name.replace(/\.[^/.]+$/, '')); // Remove file extension

    // Create preview URL
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSave = async (data: any) => {
    if (!organisationId) {
      showToast('error', 'Organization data not available');
      return;
    }

    if (activeTab === 'upload' && data.file) {
      // Step 1: Upload file to get URL
      // Step 2: The upload success callback will automatically update organisation media
      uploadFile({
        file: data.file,
        name: data.name,
        owner: {
          id: organisationId,
          resourceType: 'Organisation'
        }
      });
    } else if (activeTab === 'link' && data.url) {
      // For link uploads, directly update organisation media (no file upload needed)
      const mediaData: OrganisationMedia = {
        url: data.url,
        name: data.name,
        description: data.description || ''
      };
      updateOrganisationMedia({ media: [mediaData] });
    }
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogContent className='sm:max-w-md lg:max-w-xl'>
        <DialogHeader>
          <DialogTitle className='text-2xl font-bold'>
            Add media file
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSave)}>
          <Tabs
            defaultValue='upload'
            value={activeTab}
            onValueChange={setActiveTab}
            className='w-full'
          >
            <TabsList className='grid w-full grid-cols-2'>
              <TabsTrigger
                value='upload'
                className='data-[state=active]:border-b-2 data-[state=active]:border-blue-700'
              >
                Upload File
              </TabsTrigger>
              <TabsTrigger
                value='link'
                className='data-[state=active]:border-b-2 data-[state=active]:border-blue-700'
              >
                Add Link
              </TabsTrigger>
            </TabsList>

            <TabsContent value='upload' className='mt-6'>
              <div
                className={`rounded-md border-2 border-dashed p-12 text-center ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {previewUrl ? (
                  <div className='cursor-pointer' onClick={handleImageClick}>
                    <img
                      src={previewUrl}
                      alt='Preview'
                      
                      
                      className='mx-auto max-h-48 object-contain'
                    />
                    <p className='mt-2 text-sm text-gray-500'>
                      Click to change image
                    </p>
                  </div>
                ) : (
                  <>
                    <div className='mb-4 flex justify-center'>
                      <div className='relative flex h-16 w-16 items-center justify-center rounded-md bg-blue-100'>
                        <svg
                          width='40'
                          height='40'
                          viewBox='0 0 24 24'
                          fill='none'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <path
                            d='M3 19H21V8.5H17.5V5H3V19ZM4.5 6.5H16V8.5H4.5V6.5ZM8 10H19.5V17.5H4.5V10H8ZM16 11.5V16H17.5V11.5H16Z'
                            fill='#4285F4'
                          />
                          <path
                            d='M18.5 8C19.3284 8 20 7.32843 20 6.5C20 5.67157 19.3284 5 18.5 5C17.6716 5 17 5.67157 17 6.5C17 7.32843 17.6716 8 18.5 8Z'
                            fill='#4285F4'
                          />
                        </svg>
                        <div className='absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white'>
                          <svg
                            width='14'
                            height='14'
                            viewBox='0 0 15 15'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M8 2.75C8 2.47386 7.77614 2.25 7.5 2.25C7.22386 2.25 7 2.47386 7 2.75V7H2.75C2.47386 7 2.25 7.22386 2.25 7.5C2.25 7.77614 2.47386 8 2.75 8H7V12.25C7 12.5261 7.22386 12.75 7.5 12.75C7.77614 12.75 8 12.5261 8 12.25V8H12.25C12.5261 8 12.75 7.77614 12.75 7.5C12.75 7.22386 12.5261 7 12.25 7H8V2.75Z'
                              fill='currentColor'
                              fillRule='evenodd'
                              clipRule='evenodd'
                            ></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <p className='mb-1 font-medium'>
                      Select a PNG or JPG file to upload
                    </p>
                    <p className='mb-4 text-gray-500'>
                      or drag and drop it here
                    </p>
                  </>
                )}
                <input
                  type='file'
                  id='file-upload'
                  className='hidden'
                  accept='.png,.jpg,.jpeg'
                  onChange={handleFileChange}
                  ref={fileInputRef}
                />
                {!previewUrl && (
                  <Button
                    variant='outline'
                    onClick={() => fileInputRef.current?.click()}
                    type='button'
                  >
                    Select File
                  </Button>
                )}
              </div>

              <div className='mt-6 space-y-4'>
                <div className='space-y-2'>
                  <label htmlFor='description' className='text-lg font-medium'>
                    Description *
                  </label>
                  <Textarea
                    id='description'
                    placeholder='Description'
                    className='resize-none'
                    {...form.register('description')}
                  />
                  <FormError error={form.formState.errors.description} />
                </div>

                <div className='space-y-2'>
                  <label htmlFor='save-as' className='text-lg font-medium'>
                    Save file as
                  </label>
                  <Input
                    id='save-as'
                    placeholder='What do you want this file to be saved as'
                    {...form.register('name')}
                  />
                  <FormError error={form.formState.errors.url} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value='link' className='mt-6 space-y-4'>
              <div className='space-y-2'>
                <label htmlFor='file-url' className='text-lg font-medium'>
                  File url
                </label>
                <Input
                  id='file-url'
                  placeholder='Enter link here'
                  {...form.register('url')}
                />
                <FormError error={form.formState.errors.url} />
              </div>

              <div className='space-y-2'>
                <label htmlFor='description' className='text-lg font-medium'>
                  Description *
                </label>
                <Textarea
                  id='description'
                  placeholder='Description'
                  className='resize-none'
                  {...form.register('description')}
                />
                <FormError error={form.formState.errors.description} />
              </div>

              <div className='space-y-2'>
                <label htmlFor='save-as-link' className='text-lg font-medium'>
                  Save file as *
                </label>
                <Input
                  id='save-as-link'
                  placeholder='What do you want this file to be saved as'
                  {...form.register('name')}
                />
                <FormError error={form.formState.errors.name} />
              </div>
            </TabsContent>
          </Tabs>

          <div className='mt-6 flex justify-between'>
            <DialogClose asChild>
              <Button variant='outline' className='mr-2 w-full' type='button'>
                Cancel
              </Button>
            </DialogClose>
            <Button
              className='ml-2 w-full'
              type='submit'
              disabled={isUploading || isUpdatingMedia}
            >
              {isUploading ? 'Uploading file...' : isUpdatingMedia ? 'Saving media...' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadMediaModal;

const FormError = ({ error }: { error?: { message?: string } }) =>
  error ? <p className='text-sm text-red-500'>{error.message}</p> : null;
